import React from 'react';
import { Line, SchematicShell } from './SchematicPrimitives';
import { surroundPanelThickness } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Surround — two vertical jamb panels flanking a slot. Registered for
 * `fridge-surround`, `oven-surround` and `laundry-surround`, exactly as
 * the 3D side maps all three to `fridgeSurroundRenderer`: the same two
 * full-height side panels at the machine's proportions (a laundry
 * surround is 28" × 36" × 31", stopping at the countertop underside).
 * The renderer emits only the two panels (no header, no cap); an
 * over-fridge upper cabinet is materialized separately when needed.
 * The elevation matches: two vertical jamb lines defining the cavity,
 * no closing header.
 *
 * Variant fields:
 *   - obj.dimensions.sidePanelThickness — actual jamb thickness in
 *     meters, written by buildFridgeSurround.js / buildLaundrySurround.
 *     Resolved through `surroundPanelThickness` (proceduralSpecs.js) so
 *     the fallback is the SAME constant the renderer draws.
 *
 * Side views: the full panel face — a depth×height rectangle, which is
 * all a side panel is from its own side. Symmetric left ↔ right.
 */

export function resolveFridgeSurroundJamb(obj = {}, width = 0) {
  // Defensive cap at 1/4 width so we never render an opaque rectangle if
  // the data is malformed.
  return Math.min(surroundPanelThickness(obj?.dimensions), width / 4);
}

export function FridgeSurroundSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  obj = {},
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const jamb = resolveFridgeSurroundJamb(obj, width);
  const stroke = { strokeColor, strokeWidth };
  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Left jamb panel — outer + inner edges. */}
      <Line x1={0} y1={0} x2={0} y2={height} {...stroke} />
      <Line x1={jamb} y1={0} x2={jamb} y2={height} {...stroke} />
      {/* Right jamb panel — outer + inner edges. */}
      <Line
        x1={width - jamb}
        y1={0}
        x2={width - jamb}
        y2={height}
        {...stroke}
      />
      <Line x1={width} y1={0} x2={width} y2={height} {...stroke} />
      {/* Top + bottom edges of the jamb panels only (no header across
          the cavity). */}
      <Line x1={0} y1={0} x2={jamb} y2={0} {...stroke} />
      <Line x1={width - jamb} y1={0} x2={width} y2={0} {...stroke} />
      <Line x1={0} y1={height} x2={jamb} y2={height} {...stroke} />
      <Line x1={width - jamb} y1={height} x2={width} y2={height} {...stroke} />
    </SchematicShell>
  );
}
FridgeSurroundSchematic.supportsSideViews = true;
